import { addDays, sameDay, startOfWeek } from "../core/calendar";

const parseIsoDate = (value) => {
    const parts = String(value || "").split("-").map(Number);
    return parts.length === 3 && parts.every(Number.isFinite)
        ? new Date(parts[0], parts[1] - 1, parts[2])
        : new Date();
};

const toIsoDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return year + "-" + month + "-" + day;
};

const mountTimesheetWeekCalendar = (page) => {
    if (!(page instanceof HTMLElement) || page.dataset.timesheetCalendarReady === "true") {
        return;
    }

    const title = page.querySelector("[data-timesheet-calendar-title]");
    const weekdays = page.querySelector("[data-timesheet-calendar-weekdays]");
    const grid = page.querySelector("[data-timesheet-calendar-grid]");
    const previous = page.querySelector("[data-timesheet-calendar-previous]");
    const next = page.querySelector("[data-timesheet-calendar-next]");
    if (!(title instanceof HTMLElement) ||
        !(weekdays instanceof HTMLElement) ||
        !(grid instanceof HTMLElement) ||
        !(previous instanceof HTMLButtonElement) ||
        !(next instanceof HTMLButtonElement)) {
        return;
    }

    page.dataset.timesheetCalendarReady = "true";
    const locale = document.documentElement.lang || "en-GB";
    const selectedDate = parseIsoDate(page.dataset.timesheetWeek);
    const monthTitle = new Intl.DateTimeFormat(locale, {
        month: "long",
        year: "numeric",
        calendar: "gregory"
    });
    const dayLabel = new Intl.DateTimeFormat(locale, {
        weekday: "short",
        calendar: "gregory"
    });
    const fullDateLabel = new Intl.DateTimeFormat(locale, {
        dateStyle: "full",
        calendar: "gregory"
    });
    let visibleMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);

    const buildHref = (date) => {
        const url = new URL(page.dataset.timesheetUrl || window.location.pathname, window.location.origin);
        url.searchParams.set("week", toIsoDate(date));
        return url.pathname + url.search;
    };

    const render = () => {
        title.textContent = monthTitle.format(visibleMonth);
        weekdays.replaceChildren();
        grid.replaceChildren();

        const monday = new Date(2024, 0, 1);
        for (let index = 0; index < 7; index += 1) {
            const label = document.createElement("span");
            label.textContent = dayLabel.format(addDays(monday, index));
            weekdays.append(label);
        }

        const firstCell = startOfWeek(visibleMonth, 1);
        for (let index = 0; index < 42; index += 1) {
            const date = addDays(firstCell, index);
            const link = document.createElement("a");
            link.className = "rcd-timesheet-calendar__day";
            link.href = buildHref(date);
            link.textContent = String(date.getDate());
            link.setAttribute("aria-label", fullDateLabel.format(date));
            if (date.getMonth() !== visibleMonth.getMonth()) {
                link.classList.add("is-outside");
            }
            if (sameDay(date, selectedDate)) {
                link.classList.add("is-selected");
                link.setAttribute("aria-current", "date");
            }
            grid.append(link);
        }
    };

    previous.addEventListener("click", () => {
        visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1);
        render();
    });
    next.addEventListener("click", () => {
        visibleMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1);
        render();
    });
    render();
};

const mountTimesheetEntry = (form) => {
    if (!(form instanceof HTMLFormElement) || form.dataset.timesheetEntryReady === "true") {
        return;
    }

    const jobSelect = form.querySelector("[data-timesheet-job-select]");
    const jobLink = form.querySelector("[data-timesheet-job-link]");
    if (!(jobSelect instanceof HTMLSelectElement) || !(jobLink instanceof HTMLAnchorElement)) {
        return;
    }

    form.dataset.timesheetEntryReady = "true";
    const baseUrl = jobLink.dataset.timesheetJobUrl || jobLink.pathname;
    const updateJobLink = () => {
        const jobId = jobSelect.value;
        jobLink.href = jobId
            ? baseUrl + "?id=" + encodeURIComponent(jobId)
            : baseUrl;
        jobLink.toggleAttribute("aria-disabled", !jobId);
    };

    jobSelect.addEventListener("change", updateJobLink);
    updateJobLink();
};

export const setupTimesheetEntries = (root = document) => {
    if (!root?.querySelectorAll) {
        return;
    }

    if (root.matches?.("[data-timesheet-entry]")) {
        mountTimesheetEntry(root);
    }

    root.querySelectorAll("[data-timesheet-entry]").forEach(mountTimesheetEntry);

    if (root.matches?.("[data-timesheet-page]")) {
        mountTimesheetWeekCalendar(root);
    }

    root.querySelectorAll("[data-timesheet-page]").forEach(mountTimesheetWeekCalendar);
};
